/**
 * Profile Page
 * ============
 * User profile page for viewing and editing personal information
 * No password fields - password changes are handled in Settings
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks';
import { useToast } from '../contexts/ToastContext';
import { Card, Input, Button, Badge, Alert } from '../components/ui';
import { DashboardLayout } from '../components/layout';
import GoogleDriveConnectionCard from '../components/users/GoogleDriveConnectionCard';
import userAPI from '../api/users';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.hash === '#integrations') {
      const el = document.getElementById('integrations');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    country_code: user?.country_code || '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate required fields
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    // Validate phone if provided (E.164: leading +, country code, max 15 digits)
    if (formData.phone) {
      const phone = formData.phone.trim();
      const digits = phone.replace(/[^\d]/g, '');
      if (!phone.startsWith('+')) {
        newErrors.phone = 'Phone must start with + and the country code (e.g., +14155552671)';
      } else if (/[^\d]/.test(phone.slice(1))) {
        newErrors.phone = 'Phone may only contain digits after the leading + (no spaces, dashes, or letters)';
      } else if (digits.length > 15) {
        newErrors.phone = `Phone number is too long — max 15 digits, you entered ${digits.length}`;
      } else if (!/^\+[1-9]\d{1,14}$/.test(phone)) {
        newErrors.phone = 'Enter a valid phone number in international format (e.g., +14155552671)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        country_code: formData.country_code || null,
      };

      const updatedUser = await userAPI.updateProfile(updateData);

      // Update user in context
      updateUser(updatedUser);

      // Show success message
      showToast('Profile updated successfully', 'success');

      // Reset form and exit edit mode
      setFormData({
        first_name: updatedUser.first_name,
        last_name: updatedUser.last_name,
        phone: updatedUser.phone || '',
        country_code: updatedUser.country_code || '',
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Profile update error:', error);

      // Extract error message from API response (supports standardized error format)
      const errorMessage =
        error.response?.data?.detail?.error?.message ||
        error.response?.data?.message ||
        'Failed to update profile';
      showToast(errorMessage, 'error');

      // Set field-specific errors if available (from validation errors)
      const fieldErrors =
        error.response?.data?.detail?.error?.details ||
        error.response?.data?.errors;
      if (fieldErrors) {
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || '',
      country_code: user?.country_code || '',
    });
    setErrors({});
    setIsEditing(false);
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white">
            My Profile
          </h1>
          <p className="text-gray-600 mt-2">Manage your personal information</p>
        </div>

        {/* Profile Card */}
        <Card className="mb-8">
          <div className="p-6 sm:p-8">
            {/* Header with Avatar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-6 border-b border-gray-200 mb-8">
              <div className="flex items-center gap-4 mb-4 sm:mb-0 min-w-0">
                {/* Avatar */}
                <div className="w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center text-white text-2xl font-bold shadow-medium flex-shrink-0">
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </div>

                <div className="min-w-0">
                  <h2 className="text-2xl font-semibold text-gray-900 break-words">
                    {user?.first_name} {user?.last_name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant="primary">{user?.primary_role}</Badge>
                    <span className="text-sm text-gray-500 break-all">{user?.email}</span>
                  </div>
                </div>
              </div>

              {!isEditing && (
                <Button onClick={() => setIsEditing(true)} variant="outline" className="w-full sm:w-auto flex-shrink-0">
                  Edit Profile
                </Button>
              )}
            </div>

            {isEditing ? (
              // Edit Mode
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">
                    Personal Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="First Name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      error={errors.first_name}
                      required
                    />

                    <Input
                      label="Last Name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      error={errors.last_name}
                      required
                    />

                    <Input
                      label="Email"
                      type="email"
                      value={user?.email}
                      disabled
                      helpText="Email cannot be changed"
                    />

                    <Input
                      label="Phone Number"
                      name="phone"
                      type="tel"
                      placeholder="+14155552671"
                      value={formData.phone}
                      onChange={handleChange}
                      error={errors.phone}
                      helpText="Format: +[country code][number], digits only, max 15 digits"
                    />

                    <Input
                      label="Country Code"
                      name="country_code"
                      placeholder="US"
                      value={formData.country_code}
                      onChange={handleChange}
                      maxLength={3}
                      helpText="2-3 letter country code"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <Button type="submit" loading={loading}>
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              // View Mode
              <div className="space-y-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">
                    Personal Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        First Name
                      </label>
                      <p className="text-base text-gray-900 dark:text-gray-100">{user?.first_name}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        Last Name
                      </label>
                      <p className="text-base text-gray-900 dark:text-gray-100">{user?.last_name}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        Email
                      </label>
                      <p className="text-base text-gray-900 dark:text-gray-100">{user?.email}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        Phone Number
                      </label>
                      <p className="text-base text-gray-900 dark:text-gray-100">
                        {user?.phone || <span className="text-gray-400">Not set</span>}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        Country Code
                      </label>
                      <p className="text-base text-gray-900 dark:text-gray-100">
                        {user?.country_code || <span className="text-gray-400">Not set</span>}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Password Change Info */}
                <div className="pt-6 border-t border-gray-200">
                  <Alert variant="info">
                    To change your password, please visit the{' '}
                    <Link
                      to="/settings"
                      className="font-medium underline hover:text-primary"
                    >
                      Settings page
                    </Link>
                    .
                  </Alert>
                </div>

                {/* Roles & Permissions */}
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Roles & Permissions
                  </h3>

                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-500 block mb-3">
                      Your Roles
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {user?.roles?.map((role) => (
                        <Badge
                          key={role}
                          variant={role === user?.primary_role ? 'primary' : 'accent'}
                        >
                          {role}
                          {role === user?.primary_role && ' (Primary)'}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Alert variant="info">
                    Contact your administrator to change roles or permissions.
                  </Alert>
                </div>

                {/* Account Information */}
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">
                    Account Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        Status
                      </label>
                      <div className="mt-1">
                        <Badge variant={user?.is_active ? 'success' : 'danger'}>
                          {user?.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        Last Login
                      </label>
                      <p className="text-base text-gray-900 dark:text-gray-100">
                        {user?.last_login
                          ? new Date(user.last_login).toLocaleString()
                          : 'Never'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        Member Since
                      </label>
                      <p className="text-base text-gray-900 dark:text-gray-100">
                        {new Date(user?.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        Last Updated
                      </label>
                      <p className="text-base text-gray-900 dark:text-gray-100">
                        {new Date(user?.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Integrations */}
        <div id="integrations" className="mb-4 scroll-mt-8">
          <h2 className="text-xl font-semibold text-gray-900">Integrations</h2>
          <p className="text-sm text-gray-600 mt-1">
            Connect third-party services so Portal can read documents exchanged in your leads' email correspondence.
          </p>
        </div>
        <GoogleDriveConnectionCard />
      </div>
    </DashboardLayout>
  );
};

export default Profile;
